import JSBridge from "@xpeng/js-bridge";
import { Toast } from "@xp-design/mobile";
import dayjs from "dayjs";
import { getPlatform, priceFormat } from "../../../utils/common";
import { wxInit, myInit } from "../../../utils/mini-program";
import { getParamsFromHash, getParamsFromHref } from "@/utils/url";
import { MALL_ENTRY, PAY_RETURN_REDIRECT_URL } from "../config";
import { getBrowserEnv } from "@/utils/tools";
import type { GoodsItem } from "../types";


export function entry () {
  try {
    return getParamsFromHref?.("entry") || getParamsFromHash?.("entry") || MALL_ENTRY;
  } catch {
    return MALL_ENTRY;
  }
}

/**
 * 构造商城支付完成回跳的 redirect.html URL
 * 协议：_mode=back（出栈）+ _target=<环境>（出栈方式）
 */
export function buildPayReturnUrl() {
  const { isXPApp, isWXApp, isMyApp } = getBrowserEnv();
  let target: "xpapp" | "wxapp" | "myapp" | "browser" = "browser";
  if (isXPApp) target = "xpapp";
  else if (isWXApp) target = "wxapp";
  else if (isMyApp) target = "myapp";
  return `${PAY_RETURN_REDIRECT_URL}?_mode=back&_target=${target}`;
}

/**
 * 使用原生分享按钮，触发自定义分享事件
 * 若是IOS端，需要放入事件循环队列尾部执行，安卓端则直接执行
 */
export function defineSelfShareAction(cb: () => void) {
  const register = () => {
    try {
      JSBridge.registerHandler(
        "onActionButtonShowing",
        (data, responseCallback) => {
          // true = 显示, false = 隐藏
          const options = {
            share: true,
          };
          responseCallback(options);
        },
      );
      JSBridge.registerHandler(
        "onActionButtonClick",
        (data, responseCallback) => {
          const res = data instanceof Array ? data[0] : data;
          if (res.action === "share") {
            // 通知原生端将要拦截该事件
            responseCallback({ preventDefault: true });
            // 触发自定义的事件
            cb();
          }
        },
      );
    } catch (error) {
      console.log("defineSelfShareAction -->", error);
    }
  };

  if (getPlatform() === "iOS") {
    setTimeout(() => {
      register();
    }, 0);
  } else {
    register();
  }
}

/**
 * 格式化时间戳
 * @param timestamp 时间戳
 * @param format 格式
 */
export function formatTs(timestamp, format = "YYYY-MM-DD HH:mm:ss") {
  if (!timestamp) return "--";
  return dayjs(timestamp).format(format);
}

export const showToast = (text: string, duration = 2000) => {
  Toast.show({
    content: text,
    duration,
  });
};

/**
 * 将分转换为元，默认保留一位小数
 * @param price
 * @param fix
 * @returns
 */
export function formatPrice(price: number | string, fix = 1) {
  price = Number(price);
  if (isNaN(price)) return "";
  return (price / 100).toFixed(fix);
}

// 如果是微信小程序/支付宝小程序，需要设置页面分享信息
export const defineShareInfoInMiniProgram = ({
  shareTitle = "",
  shareIcon = "",
}) => {
  const { isWXApp, isMyApp } = getBrowserEnv();
  if (isWXApp) {
    wxInit().then((wx) =>
      wx.miniProgram.postMessage({
        data: { shareTitle, shareIcon },
      }),
    );
  } else if (isMyApp) {
    myInit().then((my) =>
      my.postMessage({
        data: { shareTitle, shareIcon },
      }),
    );
  }
};

export const getAvailableTime = (startTime: string, endTime: string) => {
  if (!startTime || !endTime) return "";

  if (startTime === "00:00" && endTime === "24:00") {
    return "全天可用";
  }

  // 正常来说全天只有上面这种小时和分钟的情况，怕数据没全部更新，加个兜底
  if (startTime === "00:00:00" && endTime === "23:59:59") {
    return "全天可用";
  }

  const startHour = +startTime.split(":")[0];
  const endHour = +endTime.split(":")[0];
  return `${startHour}-${endHour}点可用`;
};

export const getReportVipData = (data: any) => {
  if (!data) return {};
  let protection = "不在保护期";
  let validity = "";
  if (data?.levelProtected) {
    const diff = (data?.protectionEndTime - Date.now()) / 1000 / 3600 / 24;
    if (diff < 0.5) protection = "在保护期（今天12点到期）";
    else if (diff < 1.5) protection = "在保护期（明天12点到期）";
    protection = `在保护期（${Math.floor(diff)}天后到期）`;
  }
  if (data?.memberType === 1) {
    validity = "车主终身有效";
  } else if (data?.memberType === 0) {
    validity = `非车主剩余${Math.floor((data?.invTime - Date.now()) / 1000 / 3600 / 24) + 1}天`;
  }

  const reportData = {
    vip_level: data?.level ? `V${data?.level}` : "V0",
    vip_protection: protection,
    vip_validity: validity,
    var_ch: entry(),
    group_base:
      data?.memberType === 1 || data?.member === 1
        ? "充电VIP成员"
        : "其他新能源车主",
  };
  console.log("getReportVipData", reportData, data);
  return reportData;
};

export const privilegesEnum = {
  1: "（权益1）会员专享价",
  2: "（权益2）每月领福利",
  3: "（权益3）会员日福利",
  4: "（权益4）购会员产品",
  5: "（权益5）会员预约充电",
};

// 过滤对象中值为null的字段
export const filterNullFields = (obj) => {
  if (!obj || typeof obj !== "object") return obj;
  const filtered = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && value !== undefined) {
      filtered[key] = value;
    }
  }
  return filtered;
};

export const getGoodsType = (componentStyle: number) => {
  // 1：一排x格；2：一排1格；3: 一排一格+二排滑动x格；4: 一排一格+x行
  if (componentStyle === 1) return "ONE_ROW_X_GOODS";
  if (componentStyle === 2) return "ONE_ROW_GOODS_BUY";
  if (componentStyle === 3) return "ONE_ROW_X_GOODS_BUY";
  if (componentStyle === 4) return "ONE_ROW_ONE_GOODS_BUY";
  return undefined;
};

const goodsTypeName = {
  ONE_ROW_X_GOODS: "一排x格",
  ONE_ROW_GOODS_BUY: "一排1格",
  ONE_ROW_X_GOODS_BUY: "一排一格+二排滑动x格",
  ONE_ROW_ONE_GOODS_BUY: "一排一格+x行",
};

// "上报购买组件类型：区分
// VIP卡购买组件（通用）、券包/储值卡购买组件（通用）、会员产品自定义"
const compStyleName = {
  buyMemberCard: "VIP卡购买组件（通用）",
  buyMemberGoods: "券包/储值卡购买组件（通用）",
  customizeBuyMember: "会员产品自定义",
};

export const getPointsType = (good) => {
  return good?.discountType === "ORIGINAL_PRICE" && !!good?.maxUseCreditAmount
    ? "显示积分价"
    : "未显示积分价";
};

export const getUnitPriceType = (good) => {
  if (good?.discountType === "ORIGINAL_PRICE") {
    if (good?.maxUseCreditAmount) {
      return "无单价显示";
    } else {
      return "原价";
    }
  } else if (good?.discountType === "SECONDS_KILL_PRICE") {
    return "秒杀价";
  } else {
    return "抵扣红包后优惠价+划线价";
  }
};

export const getBtnType = (good) => {
  return good?.goodsType === 1
    ? "VIP卡"
    : good?.goodsType === 2
      ? "VIP券包"
      : good?.goodsType === 3
        ? "VIP电卡（储值卡）"
        : "";
};

// 获取会员配置组件相关的埋点字段
export const getReportCompData = (data: any) => {
  if (!data) return {};
  return {
    ComponentCode: data?.moduleId,
    description: data?.moduleJsonData?.domName,
    comp_style:
      goodsTypeName?.[
        getGoodsType(data?.moduleJsonData?.visualStyle?.componentStyle)
      ],
    component_type: compStyleName?.[data?.moduleJsonData?.type],
    var_ch: entry(),
  };
};

// 获取商品相关的埋点字段
export const getReportGoodsData = (goodsData: any) => {
  if (!goodsData) return {};

  return {
    // points_type	VIP2.0需求新增，上报是否显示积分价，枚举值：显示积分价、未显示积分价
    points_type: getPointsType(goodsData),
    unit_price_type: getUnitPriceType(goodsData),
    var_ch: entry(),
    btnType: getBtnType(goodsData),
  };
};

export const memberTypeMap = {
  1: "1-车主无生效券包无生效电卡",
  2: "2-车主无生效券包有生效电卡",
  3: "3-车主有生效券包无生效电卡",
  4: "4-车主有生效券包有生效电卡",
  5: "5-非车主不是vip&最近30天是vip",
  6: "6-非车主非vip&最近30天不是vip",
  7: "7-非车主是vip&无生效券包无生效电卡",
  8: "8-非车主是vip&无生效券包有生效电卡",
  9: "9-非车主是vip&有生效券包无生效电卡",
  10: "10-非车主是vip&有生效券包有生效电卡",
};

export const getUserType = (userInfo, myMemberGoods) => {
  const { userCouponList, userChargeCardList } = myMemberGoods || {};
  console.log("getUserType", userInfo, myMemberGoods);
  // 是否车主
  const isCarOwner = userInfo?.memberType === 1;
  // 是否vip
  const isVip = userInfo.member;
  // 30天内是否VIP
  const isVipIn30Days =
    userInfo?.invTime > Date.now() - 30 * 24 * 60 * 60 * 1000;
  // 有无生效中电卡
  const hasActiveCard = userChargeCardList?.length > 0;
  // 有无生效中券包
  const hasActiveCoupon = userCouponList?.length > 0;

  // 匹配会员身份
  if (isCarOwner) {
    if (hasActiveCoupon) {
      if (hasActiveCard) {
        return memberTypeMap[4];
      }
      return memberTypeMap[3];
    }
    if (hasActiveCard) {
      return memberTypeMap[2];
    }
    return memberTypeMap[1];
  } else {
    if (isVip) {
      if (hasActiveCoupon) {
        if (hasActiveCard) {
          return memberTypeMap[10];
        }
        return memberTypeMap[9];
      }
      if (hasActiveCard) {
        return memberTypeMap[8];
      }
      return memberTypeMap[7];
    } else {
      if (isVipIn30Days) {
        return memberTypeMap[5];
      }
      return memberTypeMap[6];
    }
  }
};

// 角标文案前缀：仅对带优惠的 discountType 有值
const PROMOTION_LABEL_PREFIX: Partial<
  Record<GoodsItem["discountType"], string>
> = {
  COUPON_PRICE: "红包已减",
  MALL_ACT_PRICE: "优惠已减",
  MALL_COUPON_AND_ACT_PRICE: "叠加券减",
};

/**
 * 商品促销角标信息：售价低于原价时的优惠提示
 * - 仅 COUPON_PRICE / MALL_ACT_PRICE / MALL_COUPON_AND_ACT_PRICE 显示
 * - 售罄、SECONDS_KILL_PRICE、ORIGINAL_PRICE 或异常数据（amount <= 0）不显示
 * - 金额统一按 salePrice - discountBuyPrice 计算
 * - couponInvolved 表示是否涉及券：决定是否展示" >"跳转入口和绑定弹窗点击
 */
export const getPromotionLabel = (good?: Partial<GoodsItem> | null) => {
  if (!good || good.soldOut) return null;
  const prefix =
    PROMOTION_LABEL_PREFIX[good.discountType as GoodsItem["discountType"]];
  if (!prefix) return null;
  const amount = (good.salePrice ?? 0) - (good.discountBuyPrice ?? 0);
  if (!(amount > 0)) return null;
  return {
    prefix,
    amountText: priceFormat(amount),
    text: `${prefix}${priceFormat(amount)}元`,
    couponInvolved:
      good.discountType === "COUPON_PRICE" ||
      good.discountType === "MALL_COUPON_AND_ACT_PRICE",
  };
};
